import {
  AppBar,
  Box,
  Collapse,
  Container,
  Stack,
  useScrollTrigger,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";

import useApi from "../hooks/useApi.js";
import ProfileHeader from "../features/profile/ProfileHeader.js";
import ProfileCollection from "../features/curate/ProfileCollection.js";
import ProfileStandouts from "../features/profile/ProfileStandouts.js";
import ProfileCollections from "../features/profile/ProfileCollections.js";
import DashboardInProgress from "../features/profile/DashboardInProgress.js";
import DashboardRecentlyFinished from "../features/profile/DashboardRecentlyFinished.js";
import DashboardGettingStarted from "../features/profile/DashboardGettingStarted.js";
import DashboardProgress from "../features/profile/DashboardProgress.js";
import Loading from "../components/Loading.js";
import Offline from "../components/Offline.js";
import MotionInView from "../components/MotionInView.js";
import { useAuth } from "../context/authContext.js";
import { useDocumentMeta } from "../hooks/useDocumentMeta.js";
import ProfileCounts from "../features/profile/ProfileCounts.js";
import SectionErrorBoundary from "../components/SectionErrorBoundary.js";
import HideOnScroll from "../components/HideOnScroll.js";
import HeaderMenuButton from "../components/HeaderMenuButton.js";
import ContentTypePills from "../components/ContentTypePills.js";
import EmptySectionState from "../components/EmptySectionState.js";
import PlaceholderFade from "../components/PlaceholderFade.js";
import SectionHeader from "../components/SectionHeader.js";
import { CrownIcon } from "../components/icons";
import useResizeObserver from "../hooks/useResizeObserver.js";
import {
  useAchievements,
  useInProgress,
  useStandouts,
} from "../hooks/useCustomQuery.js";
import {
  formatCount,
  getContentLabel,
  getContentTypeFromSlug,
  getContentTypeSlug,
  orderContentTypesByCount,
} from "../utils/utils.js";
import { PROFILE_KIND } from "../utils/listSchema.js";

// formatCount renders 0 as "—" (a stat-card "no data" convention) — wrong
// here, since 0 is a real value in a title ("0 movies"), not an absent stat.
function formatTypeCount(type, count) {
  const formatted = count === 0 ? "0" : formatCount(count);
  return `${formatted} ${getContentLabel({ type, plural: count !== 1, lowercase: true })}`;
}

// libraryCounts comes from get_user_counts, keyed by ContentType value
// ("movie"|"tvshow"|"book") — `.total` is every status (queued/started/
// abandoned/finished), the same field the content type pills show, so the
// title's per-type counts agree with what the page itself displays.
function buildProfileTitle(profile) {
  const counts = profile.libraryCounts ?? {};
  const movieCount = counts.movie?.total ?? 0;
  const tvCount = counts.tvshow?.total ?? 0;
  const bookCount = counts.book?.total ?? 0;

  return `@${profile.username}'s profile on Curate · ${formatTypeCount("movie", movieCount)}, ${formatTypeCount("tvshow", tvCount)}, ${formatTypeCount("book", bookCount)} and counting!`;
}

/**
 * A user's page: header → sticky content type pills → the active tab.
 *
 * Your own profile has two tabs: Dashboard (the default, at /u/:username) and
 * Profile (/u/:username/profile). Visitors only ever see Profile. Both tabs
 * are filtered by the pills, kept in `?type=` so it survives reloads and back
 * navigation; no param means All.
 *
 * Dashboard tab: Getting started (until done), what you're in the
 * middle of (On the go, hidden when empty), what you've recently finished,
 * then Your progress (badges, later goals).
 *
 * Profile tab: all-time top 5 → this year's standouts → public collections →
 * status counts. All shows every type's top 5 (most tracked first) and runs the
 * other sections across all types. Library totals sit in the pills.
 *
 * Empty sections: the owner sees every one, with prompts to fill it (on All,
 * empty top 5s collapse to one row). The counts hide only when nothing is
 * tracked. Visitors don't see other empty sections; when nothing else is left,
 * the counts lead and a single "hasn't curated anything yet" ("any movies
 * yet", when filtered to a type) fills the page.
 */
export default function Profile({ tab = "dashboard" }) {
  const { toggleDrawer } = useOutletContext() || {};
  const { fetchProfileList, fetchUserProfile } = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { username: paramUsername } = useParams();
  const isOwnProfile = !paramUsername || user?.username === paramUsername;
  const profileUsername = isOwnProfile ? user?.username : paramUsername;
  const activeTab = isOwnProfile ? tab : "profile";

  const contentType = getContentTypeFromSlug(searchParams.get("type")) ?? "all";

  // Pills hide on scroll only once they've stuck, i.e. past the header. A
  // callback ref, because the header first mounts after the loading state —
  // useResizeObserver only attaches when the ref object changes.
  const [headerEl, setHeaderEl] = useState(null);
  const headerRef = useMemo(() => ({ current: headerEl }), [headerEl]);
  const { height: headerHeight } = useResizeObserver(headerRef);
  // True once scrolled past the header, i.e. the pill bar is stuck
  const isPillBarStuck = useScrollTrigger({
    disableHysteresis: true,
    threshold: headerHeight,
  });

  const handleContentTypeChange = (value) => {
    setSearchParams(
      value === "all" ? {} : { type: getContentTypeSlug(value) },
      { replace: true },
    );
  };

  const handleTabChange = (newTab) => {
    navigate({
      pathname:
        newTab === "profile"
          ? `/u/${profileUsername}/profile`
          : `/u/${profileUsername}`,
      search: searchParams.toString(),
    });
  };

  const profileQuery = useQuery({
    queryKey: ["profile", profileUsername],
    queryFn: () => fetchUserProfile(profileUsername),
    enabled: !!profileUsername,
  });

  const profile = profileQuery.data;
  useDocumentMeta({
    title: profile ? buildProfileTitle(profile) : undefined,
  });

  // Library totals (every status) per type, shown in and ordering the pills
  const libraryCounts = Object.fromEntries(
    Object.entries(profile?.libraryCounts ?? {}).map(([type, counts]) => [
      type,
      counts.total,
    ]),
  );

  // One top 5 per type: every type, most tracked first, for All; else just the one
  const top5Types =
    contentType === "all"
      ? orderContentTypesByCount(libraryCounts).map((option) => option.value)
      : [contentType];
  const top5Queries = useQueries({
    queries: top5Types.map((type) => ({
      queryKey: [
        "collection",
        "profile",
        {
          profileKind: PROFILE_KIND.TOP5_ALLTIME,
          contentType: type,
          username: profileUsername,
        },
      ],
      queryFn: () =>
        fetchProfileList(type, PROFILE_KIND.TOP5_ALLTIME, profileUsername),
      enabled: !!profileUsername && activeTab === "profile",
      // Keep the previous content type's top 5 up while switching, so the
      // page doesn't collapse and lose the scroll position — same user only
      placeholderData: (previousData, previousQuery) =>
        previousQuery?.queryKey[2]?.username === profileUsername
          ? previousData
          : undefined,
    })),
  });

  // Visitors only: whether standouts are empty. Same query as ProfileStandouts,
  // so it's shared rather than fetched twice
  const standoutsQuery = useStandouts(profileUsername, contentType, {
    enabled: !isOwnProfile,
  });

  // Dashboard only: whether On the go is empty, to leave it out (an empty
  // wrapper would still take a gap). Same query as DashboardInProgress, so
  // it's shared rather than fetched twice
  const inProgressQuery = useInProgress(
    activeTab === "dashboard" ? profileUsername : null,
    contentType,
  );

  // Dashboard only: whether there's a Getting started row, so an empty one
  // doesn't leave a gap at the top. Same query as the progress sections
  const achievementsQuery = useAchievements(contentType, {
    enabled: activeTab === "dashboard",
  });
  const hasGettingStarted = !!achievementsQuery.data?.some(
    (row) => row.kind === "checklist",
  );

  if (profileQuery.isLoading) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  // Server errors throw (caught by QueryErrorBoundary) so reaching here with no
  // data means a network error on first visit — nothing cached to show.
  if (profileQuery.isError) return <Offline />;

  const collectionCount =
    contentType === "all"
      ? Object.values(profile.collectionCount ?? {}).reduce((a, b) => a + b, 0)
      : profile.collectionCount?.[contentType];

  // Visitors don't see empty sections (decided once loaded, so nothing flashes
  // away). Standouts with past years still show, for the links to them.
  const isTop5Hidden = (data) => !isOwnProfile && data.items.length === 0;
  const hasVisibleTop5 = top5Queries.some(
    ({ data }) => !data || !isTop5Hidden(data),
  );
  const standouts = standoutsQuery.data;
  // The owner check matters: the disabled query still reads ProfileStandouts'
  // cached data on your own profile
  const isStandoutsHidden =
    !isOwnProfile &&
    !!standouts &&
    standouts.items.length === 0 &&
    standouts.past_years.length === 0;
  const isCollectionsHidden = !isOwnProfile && !collectionCount;

  const isOnTheGoEmpty = inProgressQuery.data?.items.length === 0;
  // Nothing tracked (in this type, or at all for All): a row of zeros says
  // nothing, so the counts go too
  const isCountsHidden =
    (contentType === "all"
      ? Object.values(libraryCounts).reduce((a, b) => a + b, 0)
      : (libraryCounts[contentType] ?? 0)) === 0;
  const isCurationEmpty =
    !isOwnProfile &&
    !hasVisibleTop5 &&
    isStandoutsHidden &&
    isCollectionsHidden;

  // "anything", or "any movies" etc. when filtered to one type
  const emptySubject =
    contentType === "all"
      ? "anything"
      : `any ${getContentLabel({ type: contentType, plural: true, lowercase: true })}`;

  // Keyed by position, not type, so switching between single types updates
  // the one top 5 in place rather than remounting it
  const top5Rows = top5Queries.map(
    ({ data, isPlaceholderData }, index) =>
      data &&
      !isTop5Hidden(data) && (
        <MotionInView key={`top5-${index}`} delay={0}>
          <PlaceholderFade active={isPlaceholderData}>
            <ProfileCollection
              list={data}
              readOnly={!isOwnProfile}
              grouped={contentType === "all"}
              finishedCount={
                profile.libraryCounts?.[data.content_type]?.finished ?? 0
              }
              // Stacked with other types' top 5s, an empty one stays one row
              collapseWhenEmpty={contentType === "all"}
            />
          </PlaceholderFade>
        </MotionInView>
      ),
  );

  return (
    <Container
      disableGutters
      sx={{
        pb: 8,
        // A blank profile fills the page as a column, so its empty state can
        // take up the space left under the counts
        ...(isCurationEmpty && {
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }),
      }}
    >
      <Box ref={setHeaderEl}>
        <ProfileHeader
          toggleDrawer={toggleDrawer}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          profile={profile}
          isOwnProfile={isOwnProfile}
        />
      </Box>

      {/* The only sticky part of the page */}
      <HideOnScroll threshold={headerHeight}>
        <AppBar
          position="sticky"
          color="inherit"
          sx={{
            top: "var(--top-offset, 0px)",
            // Bleed past the page gutter like the carousels do, so posters
            // scrolling under the stuck bar can't show at either edge
            width: "auto",
            mx: { xs: -2, sm: -3, lg: 0 },
            px: { xs: 2, sm: 3, lg: 0 },
            boxShadow: "none",
            backgroundImage: "none",
            py: 1.5,
            flexDirection: "row",
            alignItems: "center",
            overflowX: "auto",
            // Hide the scrollbar visually while keeping scroll functionality
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {/* Takes over from the header's drawer button once that has
              scrolled away, sliding the pills across rather than jumping */}
          {toggleDrawer && (
            <Collapse
              in={isPillBarStuck}
              orientation="horizontal"
              sx={{ flexShrink: 0 }}
            >
              <HeaderMenuButton onClick={toggleDrawer} sx={{ mr: 2 }} />
            </Collapse>
          )}
          <ContentTypePills
            compact
            value={contentType}
            onChange={handleContentTypeChange}
            // The menu button leaves no room for counts, which would push the
            // pills wider than the screen
            counts={toggleDrawer && isPillBarStuck ? {} : libraryCounts}
          />
        </AppBar>
      </HideOnScroll>

      {activeTab === "dashboard" && (
        // pb clears the search FAB (56px, 30px off the bottom) below the last
        // carousel, which ends the page here
        <Stack spacing={2} pt={0.5} pb={3}>
          {[
            hasGettingStarted && (
              <SectionErrorBoundary
                key="getting-started"
                name="dashboard-getting-started"
              >
                <DashboardGettingStarted contentType={contentType} />
              </SectionErrorBoundary>
            ),
            !isOnTheGoEmpty && (
              <SectionErrorBoundary
                key="in-progress"
                name="dashboard-in-progress"
              >
                <DashboardInProgress
                  username={profileUsername}
                  contentType={contentType}
                />
              </SectionErrorBoundary>
            ),
            <SectionErrorBoundary
              key="recently-finished"
              name="dashboard-recently-finished"
            >
              <DashboardRecentlyFinished
                username={profileUsername}
                contentType={contentType}
              />
            </SectionErrorBoundary>,
            <SectionErrorBoundary key="progress" name="dashboard-progress">
              <DashboardProgress contentType={contentType} />
            </SectionErrorBoundary>,
          ]
            .filter(Boolean)
            .map((section, index) => (
              <MotionInView key={section.key} delay={index * 0.05}>
                {section}
              </MotionInView>
            ))}
        </Stack>
      )}

      {activeTab === "profile" && isCurationEmpty && (
        <>
          {!isCountsHidden && (
            <Box pt={0.5}>
              <ProfileCounts profile={profile} contentType={contentType} />
            </Box>
          )}
          {/* Centred in the rest of the page */}
          <Stack flexGrow={1} justifyContent="center" alignItems="center">
            <EmptySectionState
              stacked
              message={`@${profileUsername} hasn't curated ${emptySubject} yet`}
            />
          </Stack>
        </>
      )}

      {activeTab === "profile" && !isCurationEmpty && (
        <>
          {/* pt tops up the pill bar's 12px bottom padding to the 16px gap
              between sections */}
          <Stack spacing={2} pt={0.5} pb={2}>
            {/* All: one shared heading over every type's top 5, each titled
                by its type */}
            {contentType === "all"
              ? hasVisibleTop5 && (
                  <Box>
                    <SectionHeader
                      icon={<CrownIcon color="primary" />}
                      title="All-time top 5s"
                    />
                    {/* 12px matches the gap Carousel leaves under the same header
                    (8px margin + 4px padding) when there's no description */}
                    <Stack spacing={2} mt={1.5}>
                      {top5Rows}
                    </Stack>
                  </Box>
                )
              : top5Rows}

            {!isStandoutsHidden && (
              <MotionInView delay={0.05}>
                {/* Contain a failed section to itself rather than the whole profile */}
                <SectionErrorBoundary name="profile-standouts">
                  <ProfileStandouts
                    username={profileUsername}
                    contentType={contentType}
                    isOwnProfile={isOwnProfile}
                  />
                </SectionErrorBoundary>
              </MotionInView>
            )}

            {!isCollectionsHidden && (
              <MotionInView delay={0.1}>
                <SectionErrorBoundary name="profile-collections">
                  <ProfileCollections
                    username={profileUsername}
                    contentType={contentType}
                    count={collectionCount}
                    isOwnProfile={isOwnProfile}
                  />
                </SectionErrorBoundary>
              </MotionInView>
            )}

            {!isCountsHidden && (
              <MotionInView delay={0.15}>
                <ProfileCounts profile={profile} contentType={contentType} />
              </MotionInView>
            )}
          </Stack>
        </>
      )}
    </Container>
  );
}
